import {
  ImperativeMethods,
  OnChangeParams,
  Select,
  SelectOverrides,
} from 'baseui/select';
import { colors } from 'baseui/tokens';
import useCombinedRefs from 'hooks/use-combined-refs';
import useMultiSelectInputHelper from 'hooks/use-multi-select-input-helper';
import useSelectInputHelper from 'hooks/use-select-input-helper';
import useSelectInputWithoutDetailHelper from 'hooks/use-select-input-without-detail-helper';
import * as React from 'react';

import {
  AsyncTableMultiSelectInputProps,
  AsyncTableSelectInputProps,
  AsyncTableWithoutDetailSelectInputProps,
  ColumnDataType,
  TableSelectInputCoreProps,
  TableSelectInputProps,
} from './type';
import { styles } from '../styles';

const selectStyle: (props: {
  disabled?: boolean;
  popoverProps?: any;
  popoverBodyStyle?: any;
}) => SelectOverrides = ({ disabled, popoverProps, popoverBodyStyle }) => ({
  ...(disabled
    ? {
        Input: {
          component: () => null,
          style: styles.resetInputTable,
        },
        IconsContainer: {
          component: () => null,
          style: styles.resetInputTable,
        },
        Placeholder: {
          component: () => null,
        },
      }
    : {}),

  Popover: {
    props: {
      ...popoverProps,
      placement: 'auto',
      overrides: {
        Body: {
          style: {
            zIndex: 1000,
            ...popoverBodyStyle,
          },
        },
      },
    },
  },
  ControlContainer: {
    style: {
      ...styles.resetInputTable,
      ...(disabled && { backgroundColor: colors.gray50 }),
    },
  },
  Root: {
    style: styles.resetInputTable,
  },
  ValueContainer: {
    style: {
      ...styles.resetInputTable,
      paddingLeft: '8px !important',
      color: disabled ? '#6F6F6F !important' : 'black !important',
    },
  },
  Tag: {
    props: {
      closeable: !disabled,
      overrides: {
        Root: {
          style: ({ $theme }) => ({
            backgroundColor: $theme.colors.primaryDark,
            borderColor: $theme.colors.primaryDark,
            color: 'white !important',
            '-webkit-text-fill-color': 'white !important',
          }),
        },
      },
    },
  },
  DropdownListItem: {
    style: ({ $theme }) => ({
      position: 'relative',
    }),
  },
});

const CoreTableSelectInput = React.forwardRef<any, TableSelectInputCoreProps>(
  (props, ref) => {
    const { disabled, overrides, isMulti, ...rest } = props;
    //TODO: if any problem with useCombinedRef
    const innerRef = React.useRef<HTMLInputElement | null>(null);
    const combinedRef = useCombinedRefs<HTMLInputElement>(ref, innerRef);
    const controlRef = React.useRef<ImperativeMethods>(null);

    return (
      <Select
        {...rest}
        controlRef={controlRef}
        inputRef={combinedRef}
        clearable={false}
        overrides={selectStyle({
          disabled,
          popoverBodyStyle: {
            marginTop: `${(innerRef.current?.offsetHeight || 24) + 20}px`,
            marginBottom: `${(innerRef.current?.offsetHeight || 24) + 20}px`,
          },
          popoverProps: {
            ...overrides?.Popover?.props,
            ignoreBoundary: false,
          },
        })}
        onChange={(params) => {
          rest.onChange?.(params);
          const label = params.option?.label as string | undefined;
          if (!isMulti) {
            controlRef.current?.setDropdownOpen(false);
            controlRef.current?.setInputValue(label || '');
          }
        }}
      />
    );
  },
);

CoreTableSelectInput.displayName = 'CoreTableSelectInput';

function AsyncTableSelectInput<
  ListQueryData,
  ListQueryVariables,
  DetailQueryData,
  DetailQueryVariables,
>(
  props: AsyncTableSelectInputProps<
    ListQueryData,
    ListQueryVariables,
    DetailQueryData,
    DetailQueryVariables
  >,
) {
  const {
    value,
    dataType,
    selectGetLastItem,
    getMemoizedListVariables,
    listTransformer,
    paginationTransformer,
    useListQueryHook,
    onChange,
    detailTransformer,
    getMemoizedDetailVariables,
    useDetailLazyQueryHook,
    listKeyFunction,
    detailKeyFunction,
    staleTime,
    renderCreate,
    onClickCreate,
    ...rest
  } = props;

  const { options, onFocus, ...selectProps } = useSelectInputHelper({
    value,
    onSelectItem: (val) => {
      if (dataType === ColumnDataType.select) {
        onChange?.(val[0]?.id, val);
      } else {
        if (selectGetLastItem) {
          onChange?.(val[val.length - 1]?.id);
        } else {
          onChange?.(val, val);
        }
      }
    },
    useListQueryHook,
    getMemoizedListVariables,
    listTransformer,
    paginationTransformer,
    detailTransformer,
    getMemoizedDetailVariables,
    useDetailLazyQueryHook,
    listKeyFunction,
    detailKeyFunction,
    staleTime,
    renderCreate,
    onClickCreate,
  });

  return (
    <CoreTableSelectInput
      {...rest}
      {...selectProps}
      dataType={dataType}
      options={options || []}
      onFocus={(e) => {
        onFocus();
        rest.onFocus?.(e);
      }}
    />
  );
}

function AsyncTableWithoutDetailSelectInput<
  ListQueryData,
  ListQueryVariables,
  DetailQueryData,
  DetailQueryVariables,
>(
  props: AsyncTableWithoutDetailSelectInputProps<
    ListQueryData,
    ListQueryVariables,
    DetailQueryData,
    DetailQueryVariables
  >,
) {
  const {
    value,
    dataType,
    selectGetLastItem,
    getMemoizedListVariables,
    listTransformer,
    paginationTransformer,
    useListQueryHook,
    onChange,
    listKeyFunction,
    staleTime,
    renderCreate,
    onClickCreate,
    ...rest
  } = props;

  const { options, onFocus, ...selectProps } =
    useSelectInputWithoutDetailHelper({
      value,
      onSelectItem: (val) => {
        if (dataType === ColumnDataType.select) {
          onChange?.(val[0], val);
        } else {
          if (selectGetLastItem) {
            onChange?.(val[val.length - 1]?.id);
          } else {
            onChange?.(val, val);
          }
        }
      },
      useListQueryHook,
      getMemoizedListVariables,
      listTransformer,
      paginationTransformer,
      renderCreate,
      onClickCreate,
    });

  return (
    <CoreTableSelectInput
      {...rest}
      {...selectProps}
      dataType={dataType}
      options={options || []}
      onFocus={(e) => {
        onFocus();
        rest.onFocus?.(e);
      }}
    />
  );
}

function AsyncTableMultiSelectInput<
  ListQueryData,
  ListQueryVariables,
  DetailQueryData,
  DetailQueryVariables,
>(
  props: AsyncTableMultiSelectInputProps<
    ListQueryData,
    ListQueryVariables,
    DetailQueryData,
    DetailQueryVariables
  >,
) {
  const {
    value,
    dataType,
    selectGetLastItem,
    getMemoizedListVariables,
    listTransformer,
    paginationTransformer,
    useListQueryHook,
    onChange,
    listKeyFunction,
    renderCreate,
    onClickCreate,
    ...rest
  } = props;

  const { options, onFocus, onBlur, ...selectProps } =
    useMultiSelectInputHelper({
      value,
      onSelectItem: (val) => {
        if (dataType === ColumnDataType.select) {
          onChange?.(val[0]?.id, val);
        } else {
          if (selectGetLastItem) {
            onChange?.(val[val.length - 1]?.id);
          } else {
            onChange?.(val, val);
          }
        }
      },
      useListQueryHook,
      getMemoizedListVariables,
      listTransformer,
      paginationTransformer,
      listKeyFunction,
      renderCreate,
      onClickCreate,
    });

  return (
    <CoreTableSelectInput
      {...rest}
      {...selectProps}
      dataType={dataType}
      options={options || []}
      onFocus={(e) => {
        onFocus();
        rest.onFocus?.(e);
      }}
      onBlur={(e) => {
        onBlur();
        rest.onBlur?.(e);
      }}
      isMulti
    />
  );
}

function SyncTableSelectInput<
  ListQueryData,
  ListQueryVariables,
  DetailQueryData,
  DetailQueryVariables,
>(
  props: TableSelectInputProps<
    ListQueryData,
    ListQueryVariables,
    DetailQueryData,
    DetailQueryVariables
  >,
) {
  const {
    dataType,
    selectGetLastItem,
    value,
    options,
    onChange,
    setIsMenuOpen,
  } = props;

  return (
    <CoreTableSelectInput
      {...props}
      value={
        dataType === ColumnDataType.selectMulti && !selectGetLastItem
          ? value || []
          : (options || []).filter((curr) => curr?.id === value)
      }
      maxDropdownHeight="180px"
      options={options || []}
      onChange={(params: OnChangeParams) => {
        if (dataType === ColumnDataType.select) {
          onChange?.(params.value[0]?.id, params.value);
        } else {
          if (selectGetLastItem) {
            onChange?.(params.value[params.value.length - 1]?.id);
          } else {
            onChange?.(params.value, params.value);
          }
        }
        setIsMenuOpen?.(false);
      }}
      onFocus={(e) => {
        setIsMenuOpen?.(true);
        props.onFocus?.(e);
      }}
      onBlur={(e) => {
        setIsMenuOpen?.(false);
        props.onBlur?.(e);
      }}
    />
  );
}

export default function TableSelectInput<
  ListQueryData,
  ListQueryVariables,
  DetailQueryData,
  DetailQueryVariables,
>(
  props: TableSelectInputProps<
    ListQueryData,
    ListQueryVariables,
    DetailQueryData,
    DetailQueryVariables
  >,
) {
  const {
    useListQueryHook,
    listTransformer,
    paginationTransformer,
    getMemoizedListVariables,
    useDetailLazyQueryHook,
    detailTransformer,
    getMemoizedDetailVariables,
    ...rest
  } = props;

  if (
    useListQueryHook &&
    listTransformer &&
    paginationTransformer &&
    getMemoizedListVariables &&
    useDetailLazyQueryHook &&
    detailTransformer &&
    getMemoizedDetailVariables &&
    rest.dataType === ColumnDataType.select
  ) {
    return (
      <AsyncTableSelectInput
        {...rest}
        {...{
          useListQueryHook,
          listTransformer,
          paginationTransformer,
          getMemoizedListVariables,
          useDetailLazyQueryHook,
          detailTransformer,
          getMemoizedDetailVariables,
        }}
      />
    );
  }

  if (
    useListQueryHook &&
    listTransformer &&
    paginationTransformer &&
    getMemoizedListVariables &&
    rest.dataType === ColumnDataType.select
  ) {
    return (
      <AsyncTableWithoutDetailSelectInput
        {...rest}
        {...{
          useListQueryHook,
          listTransformer,
          paginationTransformer,
          getMemoizedListVariables,
        }}
      />
    );
  }

  if (
    useListQueryHook &&
    listTransformer &&
    paginationTransformer &&
    getMemoizedListVariables &&
    rest.dataType === ColumnDataType.selectMulti
  ) {
    return (
      <AsyncTableMultiSelectInput
        {...rest}
        {...{
          useListQueryHook,
          listTransformer,
          paginationTransformer,
          getMemoizedListVariables,
          useDetailLazyQueryHook,
          detailTransformer,
          getMemoizedDetailVariables,
        }}
      />
    );
  }
  return <SyncTableSelectInput {...rest} />;
}
